import type { CustomAppAccess } from "src/api/generated/erp/customApps/runtime/customAppAccess.ts";
import type { EnvironmentId } from "src/storage/appState/types.ts";
import { CUSTOM_APP_WINDOW } from "src/urls.ts";
import { join_paths } from "src/utils/paths.ts";

const LAUNCH_REQUEST_MESSAGE_TYPE = "aavo:custom-app-window:launch-request";
const LAUNCH_MESSAGE_TYPE = "aavo:custom-app-window:launch";
const CLOSED_POLL_INTERVAL_MS = 500;
const MAX_WINDOW_DIMENSION = 10_000;
const MAX_WINDOW_PARAMS_JSON_LENGTH = 100_000;

/** The window an app instance runs in. A main window has no parent. */
export interface CustomAppWindowContext {
	windowId: string;
	parentWindowId: string | null;
	params: Record<string, unknown>;
}

/**
 * The opener's frame access. A null revision is the current user's dev build. Popups get the viewer's permissions
 * from the opener because they run outside the signed-in views that load them.
 */
export type CustomAppWindowAccess = Omit<CustomAppAccess, "customAppRevisionId"> & {
	customAppRevisionId: number | null;
	viewerPermissions: string[];
};

/** Everything a popup needs to run the same app revision or live preview as its parent window. */
export interface CustomAppWindowLaunch {
	customAppId: number;
	title: string;
	access: CustomAppWindowAccess;
	/** The opener's environment. The popup sends its requests, such as access refreshes, in this environment. */
	environmentId: EnvironmentId;
	window: CustomAppWindowContext;
}

export interface CustomAppWindowOpenRequest {
	params: Record<string, unknown>;
	width?: number;
	height?: number;
}

export interface CustomAppWindowManagerOptions {
	customAppId: number;
	title: string;
	access: CustomAppWindowAccess;
	environmentId: EnvironmentId;
	/** The window of the app instance that opens the popups. */
	windowId: string;
	onClosed: (windowId: string) => void;
	host?: Window;
}

export type CustomAppWindowManager = ReturnType<typeof createCustomAppWindowManager>;

/**
 * Opens popups that run the same app revision with the same access as the opening app instance. Popups ask their
 * opener for the launch, so they can also reload. Popups close when the manager is disposed or the page goes away.
 */
export function createCustomAppWindowManager(options: CustomAppWindowManagerOptions) {
	const host = options.host ?? window;
	const children = new Map<string, Window>();
	const paramsByWindowId = new Map<string, Record<string, unknown>>();
	let disposed = false;

	function onMessage(event: MessageEvent<unknown>) {
		if (event.origin !== host.location.origin || !isRecord(event.data)) return;
		if (event.data.type !== LAUNCH_REQUEST_MESSAGE_TYPE || typeof event.data.windowId !== "string") return;
		const child = children.get(event.data.windowId);
		if (child == null || event.source !== child) return;
		const launch: CustomAppWindowLaunch = {
			customAppId: options.customAppId,
			title: options.title,
			access: options.access,
			environmentId: options.environmentId,
			window: {
				windowId: event.data.windowId,
				parentWindowId: options.windowId,
				params: paramsByWindowId.get(event.data.windowId) ?? {},
			},
		};
		child.postMessage({ type: LAUNCH_MESSAGE_TYPE, launch }, host.location.origin);
	}

	const closedPoll = host.setInterval(() => {
		for (const [windowId, child] of children) {
			if (!child.closed) continue;
			children.delete(windowId);
			paramsByWindowId.delete(windowId);
			options.onClosed(windowId);
		}
	}, CLOSED_POLL_INTERVAL_MS);

	function closeAll() {
		for (const child of children.values()) child.close();
	}

	host.addEventListener("message", onMessage);
	host.addEventListener("pagehide", closeAll);

	return {
		/** Returns the new window's ID, or null when the browser blocked the popup. Call during a user action. */
		open(request: CustomAppWindowOpenRequest): string | null {
			if (disposed) throw new Error("Custom App window manager is disposed.");
			const windowId = crypto.randomUUID();
			const url = new URL(join_paths([CUSTOM_APP_WINDOW, windowId]), host.location.origin);
			const features = ["popup"];
			if (request.width != null) features.push(`width=${request.width}`);
			if (request.height != null) features.push(`height=${request.height}`);
			const child = host.open(url.href, `aavo-custom-app-window-${windowId}`, features.join(","));
			if (child == null) return null;
			children.set(windowId, child);
			paramsByWindowId.set(windowId, structuredClone(request.params));
			return windowId;
		},
		/** Returns false when this app instance has no such open window. */
		close(windowId: string): boolean {
			const child = children.get(windowId);
			if (child == null || child.closed) return false;
			child.close();
			return true;
		},
		dispose() {
			if (disposed) return;
			disposed = true;
			host.clearInterval(closedPoll);
			host.removeEventListener("message", onMessage);
			host.removeEventListener("pagehide", closeAll);
			closeAll();
			children.clear();
			paramsByWindowId.clear();
		},
	};
}

/** True on the page of a Custom App popup window, which shows only the app. */
export function isCustomAppWindowPath(pathname: string): boolean {
	return pathname.startsWith(`${join_paths([CUSTOM_APP_WINDOW])}/`);
}

/** Validates an app's open request; returns an error message for an invalid one. */
export function validateCustomAppWindowOpenRequest(request: CustomAppWindowOpenRequest): string | null {
	for (const [name, value] of [
		["width", request.width],
		["height", request.height],
	] as const) {
		if (value != null && (!Number.isInteger(value) || value < 1 || value > MAX_WINDOW_DIMENSION))
			return `Window ${name} must be an integer between 1 and ${MAX_WINDOW_DIMENSION}.`;
	}
	let json: string | undefined;
	try {
		json = JSON.stringify(request.params);
	} catch {
		json = undefined;
	}
	if (json === undefined || !isRecord(request.params)) return "Window params must be a JSON object.";
	if (json.length > MAX_WINDOW_PARAMS_JSON_LENGTH)
		return `Window params must not exceed ${MAX_WINDOW_PARAMS_JSON_LENGTH} characters as JSON.`;
	return null;
}

/**
 * Asks the opener for this popup's launch. Resolves to null when there is no opener or it no longer knows the window,
 * for example because the opening app was closed.
 */
export function requestCustomAppWindowLaunch(
	windowId: string,
	{ host = window, timeoutMs = 5000 }: { host?: Window; timeoutMs?: number } = {},
): Promise<CustomAppWindowLaunch | null> {
	const opener = host.opener as Window | null;
	if (opener == null || opener.closed) return Promise.resolve(null);
	return new Promise((resolve) => {
		const timer = host.setTimeout(() => finish(null), timeoutMs);
		function finish(launch: CustomAppWindowLaunch | null) {
			host.clearTimeout(timer);
			host.removeEventListener("message", onMessage);
			resolve(launch);
		}
		function onMessage(event: MessageEvent<unknown>) {
			if (event.origin !== host.location.origin || event.source !== opener || !isRecord(event.data)) return;
			if (event.data.type !== LAUNCH_MESSAGE_TYPE || !isRecord(event.data.launch)) return;
			const launch = event.data.launch as unknown as CustomAppWindowLaunch;
			if (launch.window?.windowId !== windowId) return;
			// An opener still running an earlier Aavo version sends no viewer permissions.
			finish({ ...launch, access: { ...launch.access, viewerPermissions: launch.access.viewerPermissions ?? [] } });
		}
		host.addEventListener("message", onMessage);
		opener.postMessage({ type: LAUNCH_REQUEST_MESSAGE_TYPE, windowId }, host.location.origin);
	});
}

/** Calls onGone once when the window that opened this popup is closed. Returns the unsubscription. */
export function watchCustomAppWindowOpener(onGone: () => void, host: Window = window): () => void {
	const timer = host.setInterval(() => {
		const opener = host.opener as Window | null;
		if (opener != null && !opener.closed) return;
		host.clearInterval(timer);
		onGone();
	}, CLOSED_POLL_INTERVAL_MS);
	return () => host.clearInterval(timer);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
